import { supabase } from '../lib/supabase';
import type {
  CreditBalance,
  CreditGrant,
  GrantRemaining,
  GrantSource,
  LedgerEntry,
  ModelRate,
  TenantSubscription,
} from '../lib/credits/types';

// Row mappers — Supabase returns snake_case, types use camelCase.
const mapRate = (r: any): ModelRate => ({
  id: r.id,
  model: r.model,
  creditsPer1kInput: Number(r.credits_per_1k_input),
  creditsPer1kOutput: Number(r.credits_per_1k_output),
  creditsPer1kCacheRead: Number(r.credits_per_1k_cache_read),
  creditsPer1kCacheWrite: Number(r.credits_per_1k_cache_write),
  effectiveFrom: r.effective_from,
  effectiveTo: r.effective_to,
  note: r.note,
});

const mapGrant = (g: any): CreditGrant => ({
  id: g.id,
  tenantId: g.tenant_id,
  subscriptionId: g.subscription_id,
  amount: Number(g.amount),
  source: g.source,
  grantedAt: g.granted_at,
  expiresAt: g.expires_at,
  paymentRef: g.payment_ref,
  note: g.note,
});

const mapGrantRemaining = (g: any): GrantRemaining => ({
  ...mapGrant(g),
  remaining: Number(g.remaining),
});

const mapLedger = (l: any): LedgerEntry => ({
  id: l.id,
  tenantId: l.tenant_id,
  userId: l.user_id,
  grantId: l.grant_id,
  type: l.type,
  amount: Number(l.amount),
  model: l.model,
  feature: l.feature,
  usageRaw: l.usage_raw,
  tokensIn: l.tokens_in,
  tokensOut: l.tokens_out,
  generationId: l.generation_id,
  rateId: l.rate_id,
  requestId: l.request_id,
  reservationId: l.reservation_id,
  expiresAt: l.expires_at,
  createdAt: l.created_at,
});

const mapSubscription = (s: any): TenantSubscription => ({
  id: s.id,
  tenantId: s.tenant_id,
  plan: s.plan,
  periodStart: s.period_start,
  periodEnd: s.period_end,
  creditAllowance: Number(s.credit_allowance),
  status: s.status,
  paymentRef: s.payment_ref,
  note: s.note,
  createdAt: s.created_at,
});

/**
 * Humanise a credit balance for topbar chips: 996,011 → "996k cr".
 * Below 10k the exact number still reads at a glance, so it stays.
 */
export function formatCredits(balance: number): string {
  const rounded = Math.round(balance);
  if (rounded >= 10_000) return `${Math.round(rounded / 1000)}k cr`;
  return `${rounded.toLocaleString()} cr`;
}

export const aiCreditService = {
  /** Current balance for a tenant. Calls the SECURITY DEFINER function. */
  async getBalance(tenantId: string): Promise<number> {
    const { data, error } = await supabase.rpc('ai_credit_balance', { p_tenant: tenantId });
    if (error) throw new Error(error.message);
    return Number(data ?? 0);
  },

  /** Full balance summary: total + per-grant breakdown + nearest expiry. */
  async getBalanceSummary(tenantId: string): Promise<CreditBalance> {
    const [balance, grants] = await Promise.all([
      this.getBalance(tenantId),
      this.listActiveGrants(tenantId),
    ]);

    const activeGrants = grants.filter((g) => g.remaining > 0);
    const nearestExpiry = activeGrants.length
      ? activeGrants
          .map((g) => g.expiresAt)
          .sort()[0]
      : null;

    return { tenantId, balance, activeGrants, nearestExpiry };
  },

  async listActiveGrants(tenantId: string): Promise<GrantRemaining[]> {
    const { data, error } = await supabase
      .from('ai_grant_remaining')
      .select('*')
      .eq('tenant_id', tenantId)
      .gt('expires_at', new Date().toISOString())
      .order('expires_at', { ascending: true });

    if (error) throw new Error(error.message);
    return (data ?? []).map(mapGrantRemaining);
  },

  async listLedger(
    tenantId: string,
    opts: { limit?: number; types?: string[] } = {},
  ): Promise<LedgerEntry[]> {
    let query = supabase
      .from('ai_credit_ledger')
      .select('*')
      .eq('tenant_id', tenantId)
      .order('created_at', { ascending: false })
      .limit(opts.limit ?? 100);

    if (opts.types?.length) {
      query = query.in('type', opts.types);
    }

    const { data, error } = await query;
    if (error) throw new Error(error.message);
    return (data ?? []).map(mapLedger);
  },

  async listCurrentRates(): Promise<ModelRate[]> {
    const { data, error } = await supabase
      .from('ai_model_rates')
      .select('*')
      .is('effective_to', null)
      .order('model', { ascending: true });

    if (error) throw new Error(error.message);
    return (data ?? []).map(mapRate);
  },

  async getActiveSubscription(tenantId: string): Promise<TenantSubscription | null> {
    const now = new Date().toISOString();
    const { data, error } = await supabase
      .from('tenant_subscriptions')
      .select('*')
      .eq('tenant_id', tenantId)
      .eq('status', 'active')
      .lte('period_start', now)
      .gte('period_end', now)
      .order('period_start', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) throw new Error(error.message);
    return data ? mapSubscription(data) : null;
  },

  /** Latest active subscription row, including expired ones (no period_end filter).
   *  Used by the client-side trial-expiry hook to compute grace/locked state. */
  async getLatestSubscription(tenantId: string): Promise<TenantSubscription | null> {
    const { data, error } = await supabase
      .from('tenant_subscriptions')
      .select('*')
      .eq('tenant_id', tenantId)
      .eq('status', 'active')
      .order('period_start', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) throw new Error(error.message);
    return data ? mapSubscription(data) : null;
  },

  // -------------------------------------------------------------------
  // Hub-only methods (super-admin enforced inside the RPC / RLS).
  // -------------------------------------------------------------------

  /** Recent reconciliation rows for the hub recon block. */
  async listReconciliations(limit = 12): Promise<AnthropicReconciliation[]> {
    const { data, error } = await supabase
      .from('ai_anthropic_reconciliations')
      .select('*')
      .order('period_end', { ascending: false })
      .limit(limit);
    if (error) throw new Error(error.message);
    return (data ?? []).map(mapReconciliation);
  },

  /** Super-admin: insert an adjustment grant against any tenant. */
  async createAdjustmentGrant(
    tenantId: string,
    amount: number,
    opts: { source: GrantSource; expiresAt: string; note?: string },
  ): Promise<CreditGrant> {
    const { data, error } = await supabase.rpc('ai_credit_create_adjustment_grant', {
      p_tenant: tenantId,
      p_amount: amount,
      p_source: opts.source,
      p_expires_at: opts.expiresAt,
      p_note: opts.note ?? null,
    });
    if (error) throw new Error(error.message);
    return mapGrant(data);
  },
};

// -------------------------------------------------------------------
// Reconciliation row type
// -------------------------------------------------------------------

export interface AnthropicReconciliation {
  id: string;
  periodStart: string;
  periodEnd: string;
  anthropicCostUsd: number;
  ledgerProviderCostUsd: number;
  ledgerTotalCredits: number;
  driftPct: number;
  ranAt: string;
}

const mapReconciliation = (r: any): AnthropicReconciliation => ({
  id: r.id,
  periodStart: r.period_start,
  periodEnd: r.period_end,
  anthropicCostUsd: Number(r.anthropic_cost_usd),
  ledgerProviderCostUsd: Number(r.ledger_provider_cost_usd),
  ledgerTotalCredits: Number(r.ledger_total_credits),
  driftPct: Number(r.drift_pct),
  ranAt: r.ran_at,
});
